import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEmailsubscriptionsV1Emailsubscription from './emailsubscriptions-v1-emailsubscription-email-subscriptions.http.js';

export interface EmailSubscription {
  /**
   * Email subscription ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Email address.
   * @format EMAIL
   */
  email?: string;
  /**
   * Indicates the recipient's opt-in or opt-out status
   * for marketing emails.
   *
   * - `NOT_SET`: No status specified. This is the default, initial value before any info about the email address is known.
   * - `PENDING`: Subscription confirmation was requested,
   * but recipient hasn't confirmed yet.
   * - `SUBSCRIBED`: Recipient has opted in to marketing emails.
   * - `UNSUBSCRIBED`: Recipient has opted out of marketing emails.
   *
   * Defaults to `NOT_SET`.
   */
  subscriptionStatus?: SubscriptionEnumStatusWithLiterals;
  /**
   * Indicates last reported status of sent emails.
   *
   * - `NOT_SET`: No status specified. This is the default, initial value before any info about the email address is known.
   * - `VALID`: Emails to this email address are being delivered successfully.
   * - `BOUNCED`: The last email to the recipient bounced or was rejected.
   * - `SPAM_COMPLAINT`: The recipient registered a spam complaint
   * with their email provider.
   * - `INACTIVE`: Multiple campaigns have been delivered to this address without any engagement from the recipient. (No emails were opened and no content was clicked.)
   * This status might impact subsequent emails sent to this address.
   *
   * Defaults to `NOT_SET`.
   */
  deliverabilityStatus?: StatusWithLiterals;
  /**
   * Date and time the email subscription was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the email subscription was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export enum SubscriptionEnumStatus {
  UNKNOWN = 'UNKNOWN',
  /** No Subscription exists */
  NOT_SET = 'NOT_SET',
  /** Pending Subscription */
  PENDING = 'PENDING',
  /** Subscribed */
  SUBSCRIBED = 'SUBSCRIBED',
  /** UnSubscribed */
  UNSUBSCRIBED = 'UNSUBSCRIBED',
}

/** @enumType */
export type SubscriptionEnumStatusWithLiterals =
  | SubscriptionEnumStatus
  | 'UNKNOWN'
  | 'NOT_SET'
  | 'PENDING'
  | 'SUBSCRIBED'
  | 'UNSUBSCRIBED';

export enum Status {
  NOT_SET = 'NOT_SET',
  /** valid/deferral */
  VALID = 'VALID',
  /** bounced/rejected/invalid */
  BOUNCED = 'BOUNCED',
  /** spam/complaint */
  SPAM_COMPLAINT = 'SPAM_COMPLAINT',
  /** valid, but no activity reported */
  INACTIVE = 'INACTIVE',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'NOT_SET'
  | 'VALID'
  | 'BOUNCED'
  | 'SPAM_COMPLAINT'
  | 'INACTIVE';

/** Triggered when an email subscription's status is changed. */
export interface EmailSubscriptionChanged {
  /** Email subscription that changed. */
  subscription?: EmailSubscription;
}

export interface GetEmailSubscriptionRequest {
  /** @format GUID */
  _id: string;
}

export interface GetEmailSubscriptionResponse {
  /** Returned email subscription */
  emailSubscription?: EmailSubscription;
}

export interface UpdateEmailSubscriptionRequest {
  /** Email subscription to update */
  subscription?: EmailSubscription;
}

export interface UpdateEmailSubscriptionResponse {
  /** Updated email subscription */
  subscription?: EmailSubscription;
}

export interface QueryEmailSubscriptionsRequest {
  /**
   * Filter options.
   * Currently, querying is supported on the `email` field
   * with the `$in` array filter.
   */
  filter: Record<string, any> | null;
  /**
   * Pagination options. For more information, see
   * [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   */
  paging?: Paging;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryEmailSubscriptionsResponse {
  /** List of subscribed emails that matched the query options. */
  subscriptions?: EmailSubscription[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface UpsertEmailSubscriptionRequest {
  /** Email subscription to update or create. */
  subscription?: EmailSubscription;
}

export interface UpsertEmailSubscriptionResponse {
  /** Updated or created email subscription. */
  subscription?: EmailSubscription;
}

export interface BulkUpsertEmailSubscriptionRequest {
  /**
   * List of email subscriptions to update or create.
   * @minSize 1
   * @maxSize 100
   */
  subscriptions: EmailSubscription[];
}

export interface BulkUpsertEmailSubscriptionResponse {
  /** List of updated or created email subscriptions. */
  results?: BulkUpsertEmailSubscriptionResult[];
  /** Numbers of successful and failed actions. */
  metadata?: Metadata;
}

export interface BulkUpsertEmailSubscriptionResult {
  /** Position of the requested email subscription in the bulk array. */
  originalIndex?: number;
  /** New or updated email subscription. */
  emailSubscription?: EmailSubscription;
  /**
   * Error information if the action failed.
   * Omitted from successful actions.
   */
  error?: Error;
}

export interface Error {
  /** Error code. */
  errorCode?: string;
  /** Message that contains details about the error. */
  message?: string;
}

export interface Metadata {
  /** Number of successful actions. */
  totalSuccess?: number;
  /** Number of failed actions. */
  totalFailure?: number;
}

export interface GenerateUnsubscribeLinkRequest {
  /**
   * Email address the unsubscribe link is for.
   * @format EMAIL
   */
  emailAddress: string;
  /** Arbitrary parameters for closing-the-loop. */
  metadata?: Record<string, string>;
  /**
   * Language for displaying unsubscribe confirmation page.
   *
   * 2-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   *
   * Default: `EN`
   * @format LANGUAGE
   */
  language?: string | null;
}

export interface GenerateUnsubscribeLinkResponse {
  /** The unsubscribe link. */
  link?: string;
}

export interface RenderUnsubscribePageRequest {
  /** Payload */
  payload?: string;
  /** Language */
  language?: string | null;
}

export interface RawHttpResponse {
  body?: Uint8Array;
  statusCode?: number | null;
  headers?: HeadersEntry[];
}

export interface HeadersEntry {
  key?: string;
  value?: string;
}

export interface ConfirmUnsubscribeActionRequest {
  /** Payload */
  payload?: string;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface V1RenderUnsubscribePageRequest {
  /** Payload */
  payload?: string;
  /** Language */
  language?: string | null;
}

export interface V1ConfirmUnsubscribeActionRequest {
  /** Payload */
  payload?: string;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EmailsubscriptionEmailSubscriptionChangedEnvelope {
  data: EmailSubscriptionChanged;
  metadata: BaseEventMetadata;
}

/** @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Email Subscriptions
 * @permissionScopeId SCOPE.DC-MANAGE.EMAIL-SUBSCRIPTIONS
 * @permissionId EMAIL_SUBSCRIPTIONS.VIEW
 * @webhook
 * @eventType com.wixpress.emailsubscriptions.v1.EmailSubscriptionChanged
 * @serviceIdentifier com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService
 * @slug email_subscription_changed
 * @documentationMaturity preview
 */
export declare function onEmailsubscriptionEmailSubscriptionChanged(
  handler: (
    event: EmailsubscriptionEmailSubscriptionChangedEnvelope
  ) => void | Promise<void>
): void;

/**
 * Get Email Subscription
 * @internal
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId EMAIL_SUBSCRIPTIONS.VIEW
 * @applicableIdentity APP
 * @returns Returned email subscription
 * @fqn com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.GetEmailSubscription
 */
export async function getEmailSubscription(
  _id: string
): Promise<
  NonNullablePaths<
    EmailSubscription,
    `email` | `subscriptionStatus` | `deliverabilityStatus`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts =
    ambassadorWixEmailsubscriptionsV1Emailsubscription.getEmailSubscription(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.emailSubscription!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves email subscriptions,
 * given the provided paging, filtering, and sorting.
 *
 * Currently, querying is supported on the `email` field
 * with the `$in` array filter.
 * For example, to query for emails `"me@my.com"` and `"you@your.org"`,
 * the filter should be formed like this:
 *
 * ```json
 * { "filter": {
 * "email": {
 * "$in": ["me@my.com", "you@your.org"]
 * }
 * }
 * }
 * ```
 *
 * To learn how to query email subscriptions, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
 * @param filter - Filter options.
 * Currently, querying is supported on the `email` field
 * with the `$in` array filter.
 * @public
 * @documentationMaturity preview
 * @requiredField filter
 * @permissionId EMAIL_SUBSCRIPTIONS.VIEW
 * @applicableIdentity APP
 * @fqn com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.QueryEmailSubscriptions
 */
export async function queryEmailSubscriptions(
  filter: Record<string, any>,
  options?: QueryEmailSubscriptionsOptions
): Promise<
  NonNullablePaths<
    QueryEmailSubscriptionsResponse,
    | `subscriptions`
    | `subscriptions.${number}.email`
    | `subscriptions.${number}.subscriptionStatus`
    | `subscriptions.${number}.deliverabilityStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    paging: options?.paging,
  });

  const reqOpts =
    ambassadorWixEmailsubscriptionsV1Emailsubscription.queryEmailSubscriptions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0]', paging: '$[1].paging' },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryEmailSubscriptionsOptions {
  /**
   * Pagination options. For more information, see
   * [Pagination](https://dev.wix.com/api/rest/getting-started/pagination).
   */
  paging?: Paging;
}

/**
 * Updates or creates an email subscription for the requested email.
 *
 * An email subscription is returned in the response
 * whether it was updated or created.
 * @public
 * @documentationMaturity preview
 * @requiredField options.subscription.email
 * @permissionId EMAIL_SUBSCRIPTIONS.MODIFY
 * @applicableIdentity APP
 * @fqn com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.UpsertEmailSubscription
 */
export async function upsertEmailSubscription(
  options?: NonNullablePaths<
    UpsertEmailSubscriptionOptions,
    `subscription.email`,
    3
  >
): Promise<
  NonNullablePaths<
    UpsertEmailSubscriptionResponse,
    | `subscription.email`
    | `subscription.subscriptionStatus`
    | `subscription.deliverabilityStatus`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subscription: options?.subscription,
  });

  const reqOpts =
    ambassadorWixEmailsubscriptionsV1Emailsubscription.upsertEmailSubscription(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { subscription: '$[0].subscription' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpsertEmailSubscriptionOptions {
  /** Email subscription to update or create. */
  subscription?: EmailSubscription;
}

/**
 * Updates or creates multiple email subscriptions.
 * @param subscriptions - List of email subscriptions to update or create.
 * @public
 * @documentationMaturity preview
 * @requiredField subscriptions
 * @permissionId EMAIL_SUBSCRIPTIONS.MODIFY
 * @applicableIdentity APP
 * @fqn com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.BulkUpsertEmailSubscription
 */
export async function bulkUpsertEmailSubscription(
  subscriptions: EmailSubscription[]
): Promise<
  NonNullablePaths<
    BulkUpsertEmailSubscriptionResponse,
    | `results`
    | `results.${number}.originalIndex`
    | `results.${number}.emailSubscription.email`
    | `results.${number}.emailSubscription.subscriptionStatus`
    | `results.${number}.emailSubscription.deliverabilityStatus`
    | `results.${number}.error.errorCode`
    | `results.${number}.error.message`
    | `metadata.totalSuccess`
    | `metadata.totalFailure`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subscriptions: subscriptions,
  });

  const reqOpts =
    ambassadorWixEmailsubscriptionsV1Emailsubscription.bulkUpsertEmailSubscription(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { subscriptions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['subscriptions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates an unsubscribe link to be shared with the relevant recipient.
 *
 * If someone clicks the **Unsubscribe** button on the confirmation page,
 * the recipient's `subscriptionStatus` is changed to `UNSUBSCRIBED`.
 * @param emailAddress - Email address the unsubscribe link is for.
 * @public
 * @documentationMaturity preview
 * @requiredField emailAddress
 * @permissionId EMAIL_SUBSCRIPTIONS.VIEW
 * @applicableIdentity APP
 * @fqn com.wixpress.emailsubscriptions.v1.EmailSubscriptionsService.GenerateUnsubscribeLink
 */
export async function generateUnsubscribeLink(
  emailAddress: string,
  options?: GenerateUnsubscribeLinkOptions
): Promise<NonNullablePaths<GenerateUnsubscribeLinkResponse, `link`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    emailAddress: emailAddress,
    metadata: options?.metadata,
    language: options?.language,
  });

  const reqOpts =
    ambassadorWixEmailsubscriptionsV1Emailsubscription.generateUnsubscribeLink(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          emailAddress: '$[0]',
          metadata: '$[1].metadata',
          language: '$[1].language',
        },
        singleArgumentUnchanged: false,
      },
      ['emailAddress', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GenerateUnsubscribeLinkOptions {
  /** Arbitrary parameters for closing-the-loop. */
  metadata?: Record<string, string>;
  /**
   * Language for displaying unsubscribe confirmation page.
   *
   * 2-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   *
   * Default: `EN`
   * @format LANGUAGE
   */
  language?: string | null;
}
